<template>
  <div v-for="(upsellChoice, index) in upsellChoices" class="upsell-box">
    <div
      :class="[
        'radio-btn-text',
        upsellChoice.quicksellDisplay === 'button'
          ? 'radio-btn-text--button'
          : '',
        upsellChoice.image ? 'radio-btn-text--with-image' : '',
      ]"
    >
      <div
        v-if="upsellChoice.quicksellDisplay !== 'button'"
        class="radio-upsell"
      >
        <input
          type="checkbox"
          role="switch"
          :id="'upsell' + upsellChoice.id"
          v-model="upsellChoice.upsellState"
          @click="(event) => toggleAction(event, upsellChoice)"
        />
      </div>
      <div v-if="upsellChoice.image" class="upsell-image">
        <img :src="upsellChoice.image" alt="" />
      </div>
      <!-- Toujours le choice upsell du catalogue pour texte + prix : quand le
           toggle est active depuis une carte non-selectionnee, v-model peut
           passer upsellState a true avant que cartItem.choice soit mute vers
           l'enfant — afficher cartItem.choice montrait alors le parent. -->
      <div class="text-upsell">
        <span>
          <ChoiceUpsellDescription :choice="upsellChoice" />
        </span>
      </div>
      <div class="price-upsell">
        <ChoicePrice :choice="upsellChoice" />
      </div>
      <div v-if="upsellChoice.quicksellDisplay === 'button'" class="upsell-add">
        <button
          type="button"
          :class="[
            'upsell-add-btn',
            upsellChoice.upsellState ? 'upsell-add-btn--added' : '',
          ]"
          :aria-pressed="upsellChoice.upsellState ? 'true' : 'false'"
          @click="buttonAction(upsellChoice)"
        >
          <svg
            v-if="upsellChoice.upsellState"
            class="upsell-add-btn__icon"
            viewBox="0 0 16 16"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M3 8.5l3.2 3.2L13 5"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg
            v-else
            class="upsell-add-btn__icon"
            viewBox="0 0 16 16"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M8 3v10M3 8h10"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          <span>{{
            upsellChoice.upsellState
              ? $t("labels.quickSellAdded")
              : $t("labels.quickSellAdd")
          }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ChoiceUpsellDescription from "@/components/choice/UpsellDescription.vue";
import ChoicePrice from "@/components/choice/Price.vue";
import AbTests from "@/services/abTests";
import CartService from "@/services/cart";
import OrderPageOffers from "@/services/orderPageOffers";
import { useCartStore } from "@/store/cart";
import { useUpselledStore } from "@/store/upselled";

export default {
  name: "CartTableUpsellList",
  props: {
    cartItem: {
      type: Object,
    },
    /** Id du choice parent de la carte (OrderPage). Evite qu'un mapping
     * upsell actif sur une autre carte ne reaffiche le toggle ici. */
    parentChoiceId: {
      type: [String, Number],
      default: null,
    },
  },
  components: {
    ChoiceUpsellDescription,
    ChoicePrice,
  },
  data() {
    return {
      cart: null,
      upsellChoices: [],
    };
  },
  computed: {
    lastCartUpdate() {
      return useCartStore().lastUpdate;
    },
  },
  mounted() {
    this.cart = this.getCart();
    this.upsellChoices = this.findUpsellChoices();
  },
  watch: {
    lastCartUpdate: {
      handler(newValue, oldValue) {
        if (!window?.globalConfig?.domainId) {
          return;
        }
        this.cart = this.getCart();
      },
    },
    cart: {
      handler(newValue, oldValue) {
        if (!window?.globalConfig?.domainId) {
          return;
        }
        this.upsellChoices = this.findUpsellChoices();
      },
      deep: true,
    },
  },
  methods: {
    getCart() {
      return useCartStore().cart;
    },
    isCartItemInStore() {
      const items = useCartStore().cart?.items ?? [];
      return items.some(
        (item) =>
          item === this.cartItem ||
          (item.choice?.id &&
            this.cartItem?.choice?.id &&
            String(item.choice.id) === String(this.cartItem.choice.id) &&
            !item.isQuickSellStack),
      );
    },
    /** Ligne panier deja presente pour ce parent/upsell (ex. auto-select). */
    resolveStoreCartLine(parentChoice, upsellChoice, options = {}) {
      const items = useCartStore().cart?.items ?? [];
      if (!items.length) {
        return null;
      }
      const parentId = String(parentChoice.id);
      const upsellId = String(upsellChoice.id);
      const stackOnly = options.stack === true;
      return (
        items.find((item) => {
          if (item.isQuickSellStack) {
            return false;
          }
          const choiceId = String(item.choice?.id ?? "");
          if (choiceId === parentId) {
            return true;
          }
          if (!stackOnly && choiceId === upsellId) {
            return true;
          }
          const downgrade = useUpselledStore().getDowngradeChoice(item.choice);
          return downgrade && String(downgrade.id) === parentId;
        }) || null
      );
    },
    resolveChoiceFromCatalog(choice) {
      if (!choice?.id || !window?.productDetails?.choices) {
        return choice;
      }
      const fromCatalog = window.productDetails.choices.find(
        (entry) => String(entry.id) === String(choice.id),
      );
      return fromCatalog ? { ...fromCatalog } : { ...choice };
    },
    findUpsellEntry(parentChoiceId) {
      return OrderPageOffers.findQuicksellEntry(
        parentChoiceId,
        window?.productDetails,
      );
    },
    resolveQuicksellMode(parentChoiceId) {
      const entry = this.findUpsellEntry(parentChoiceId);
      return entry?.mode === "stack" ? "stack" : "replace";
    },
    findStackCompanion(upsellChoiceId, parentChoiceId) {
      const parentId = String(parentChoiceId || "");
      return (
        (useCartStore().cart?.items ?? []).find(
          (item) =>
            item.isQuickSellStack &&
            String(item.choice?.id) === String(upsellChoiceId) &&
            String(item.quickSellParentChoiceId || "") === parentId,
        ) || null
      );
    },
    isUpsellActiveForParent(upsellChoiceId, parentChoiceId) {
      if (this.resolveQuicksellMode(parentChoiceId) === "stack") {
        return !!this.findStackCompanion(upsellChoiceId, parentChoiceId);
      }
      return useCartStore().cart.items.some((item) => {
        if (item.isQuickSellStack) {
          return false;
        }
        if (String(item.choice?.id) !== String(upsellChoiceId)) {
          return false;
        }
        const downgrade = useUpselledStore().getDowngradeChoice(item.choice);
        return downgrade && String(downgrade.id) === String(parentChoiceId);
      });
    },
    // En mode panier, le store upselled ne garde qu'une offre d'origine par
    // choice : un choice de la vente flash de la page deja pris en remplacement
    // sous une autre offre n'est pas propose une seconde fois. En mode radio,
    // le panier ne porte qu'une offre et la regle ne s'applique pas.
    isPageQuicksellChoiceAvailable(upsellChoiceId, parentChoiceId) {
      const upsellId = String(upsellChoiceId);
      const parentId = String(parentChoiceId);
      if (upsellId === parentId) {
        return false;
      }
      if (window?.productDetails?.limitCartTo1Item) {
        return true;
      }
      return !(useCartStore().cart?.items ?? []).some((item) => {
        if (item.isQuickSellStack || String(item.choice?.id) !== upsellId) {
          return false;
        }
        const downgrade = useUpselledStore().getDowngradeChoice(item.choice);
        return downgrade?.id != null && String(downgrade.id) !== parentId;
      });
    },
    resolveUpsellAnchorParentId() {
      if (this.parentChoiceId != null && this.parentChoiceId !== "") {
        return String(this.parentChoiceId);
      }
      const downgradeChoice = useUpselledStore().getDowngradeChoice(
        this.cartItem?.choice,
      );
      if (downgradeChoice?.id && this.isCartItemInStore()) {
        return String(downgradeChoice.id);
      }
      return String(this.cartItem?.choice?.id ?? "");
    },
    findUpsellChoices() {
      if (!window?.productDetails?.upsells) {
        return [];
      }
      const finalUpsellChoices = [];

      const anchorParentId = this.resolveUpsellAnchorParentId();
      const downgradeChoice = useUpselledStore().getDowngradeChoice(
        this.cartItem?.choice,
      );
      // Mapping upsell d'une autre carte (ex. enfant 21307 affiche en offre
      // unitaire) : ne pas reutiliser le parent 21913 ici.
      if (
        downgradeChoice?.id &&
        String(downgradeChoice.id) !== anchorParentId
      ) {
        return [];
      }

      const parentChoiceId = anchorParentId;

      const upsell = this.findUpsellEntry(parentChoiceId);
      const isPageQuicksell = upsell?.scope === "page";

      const upsellMetaByChoiceId = {};
      if (upsell) {
        const abGroups = upsell.children;
        for (const abGroup of abGroups) {
          const electedAbChoice = AbTests.electItemOfGroup(abGroup);
          if (
            isPageQuicksell &&
            !this.isPageQuicksellChoiceAvailable(
              electedAbChoice.id,
              parentChoiceId,
            )
          ) {
            continue;
          }
          upsellMetaByChoiceId[electedAbChoice.id] = {
            upsellState: this.isUpsellActiveForParent(
              electedAbChoice.id,
              parentChoiceId,
            ),
            image: electedAbChoice.image || null,
            quicksellDisplay:
              electedAbChoice.quicksellDisplay === "button"
                ? "button"
                : "toggle",
          };
        }
      }

      const choiceIds = Object.keys(upsellMetaByChoiceId);
      if (!choiceIds.length) {
        return [];
      }

      const choices = window.productDetails.choices.filter((choice) =>
        choiceIds.includes(String(choice.id)),
      );

      for (const choice of choices) {
        // Ne pas muter productDetails.choices : plusieurs UpsellList coexistent
        // sur la meme page (1 par carte en mode radio).
        finalUpsellChoices.push({
          ...choice,
          upsellState: upsellMetaByChoiceId[choice.id].upsellState,
          image: upsellMetaByChoiceId[choice.id].image,
          quicksellDisplay: upsellMetaByChoiceId[choice.id].quicksellDisplay,
        });
      }

      return finalUpsellChoices;
    },
    toggleStackAction(choice, parentChoice, upsellChoice, isDeactivating) {
      if (isDeactivating) {
        const companion = this.findStackCompanion(
          upsellChoice.id,
          parentChoice.id,
        );
        if (companion) {
          CartService.deleteItem(companion);
        }
        return;
      }

      let parentLine = this.resolveStoreCartLine(parentChoice, upsellChoice, {
        stack: true,
      });
      if (!parentLine) {
        const hasOtherOffer = useCartStore().cart.items.some(
          (item) => !item.isQuickSellStack,
        );
        if (
          window?.productDetails?.limitCartTo1Item &&
          !this.cartItem.isPostUpsell &&
          hasOtherOffer
        ) {
          CartService.emptyCart();
        }
        CartService.addToCart({
          name: this.cartItem.name,
          choice: parentChoice,
          qtyIncr: 1,
          isQuickSellStack: false,
          isPostUpsell: false,
          isFromCoupon: false,
        });
        parentLine = this.resolveStoreCartLine(parentChoice, upsellChoice, {
          stack: true,
        });
      }

      if (this.findStackCompanion(upsellChoice.id, parentChoice.id)) {
        return;
      }

      CartService.addToCart({
        name: this.cartItem.name || parentLine?.name,
        choice: upsellChoice,
        qtyIncr: 1,
        isQuickSellStack: true,
        quickSellParentChoiceId: parentChoice.id,
        isPostUpsell: false,
        isFromCoupon: false,
      });
    },
    toggleAction(event, choice) {
      // @click precede la mise a jour v-model : upsellState=true = desactivation.
      this.applyUpsellAction(choice, choice.upsellState);
    },
    buttonAction(choice) {
      const isDeactivating = !!choice.upsellState;
      this.applyUpsellAction(choice, isDeactivating);
      choice.upsellState = !isDeactivating;
    },
    applyUpsellAction(choice, isDeactivating) {
      console.log("toggleAction", choice);

      const existingDowngrade = useUpselledStore().getDowngradeChoice(
        this.cartItem.choice,
      );
      const parentChoice = this.resolveChoiceFromCatalog(
        existingDowngrade || this.cartItem.choice,
      );
      const upsellChoice = this.resolveChoiceFromCatalog(choice);
      const parentChoiceId = this.resolveUpsellAnchorParentId();
      const mode = this.resolveQuicksellMode(parentChoiceId);

      if (mode === "stack") {
        this.toggleStackAction(
          choice,
          parentChoice,
          upsellChoice,
          isDeactivating,
        );
        return;
      }

      this.cartItem.qtyIncr = 1;
      this.cartItem.choice = isDeactivating ? parentChoice : upsellChoice;

      const isRadioSingle =
        window?.productDetails?.limitCartTo1Item && !this.cartItem.isPostUpsell;
      const storeLine = this.resolveStoreCartLine(parentChoice, upsellChoice);
      // Ligne panier existante (reference store ou parent deja auto-ajoute) :
      // muter cette ligne, pas le cartItem synthetique seul.
      const itemInCart =
        isRadioSingle && (this.isCartItemInStore() || storeLine);

      // FORGE-412 — En mode radio (limitCartTo1Item=true), le contrat de
      // l'order page est "1 item maximum au cart a la fois". Le toggle
      // peut etre rende dans deux contextes :
      //
      //   1. Sous une carte SELECTIONNEE (rendue via ChoiceList.vue) :
      //      `this.cartItem` est alors une reference a l'item deja
      //      present dans `cart.items`. `addToCart` detecte
      //      l'existingItem (meme choice.id apres la mutation
      //      ci-dessus) et le splice/repush au meme emplacement : pas
      //      de duplication, le contrat est respecte naturellement.
      //
      //   2. Sous une carte NON-SELECTIONNEE (rendu OrderPage.vue
      //      FORGE-412) : `this.cartItem` est un objet synthetique qui
      //      n'est PAS dans `cart.items`. Sans cleanup explicite,
      //      `addToCart` empile un nouvel item a la suite de
      //      l'eventuel item deja present (auto-select de la premiere
      //      offre, ou item ajoute precedemment). Le cart se retrouve
      //      avec >1 items, et comme `ChoiceList` itere sur TOUS les
      //      items du cart, chaque carte selectionnee rend la totalite
      //      du cart -> 2 offres au cart -> 4 cartes visuelles.
      //
      // Solution : detecter le cas 2 (cartItem absent du cart) et
      // appliquer le meme cleanup que `AddToCart.addToCartAction`.
      if (itemInCart) {
        const line = storeLine || this.cartItem;
        line.choice = isDeactivating ? parentChoice : upsellChoice;
        line.qty = 1;
        if (line !== this.cartItem) {
          this.cartItem.choice = line.choice;
          this.cartItem.qty = line.qty;
        }
        if (isDeactivating) {
          useUpselledStore().downgradeChoice(upsellChoice);
        } else {
          useUpselledStore().upgradeChoice(parentChoice, upsellChoice);
        }
        CartService.updateCart();
      } else {
        this.cartItem.qty = 0;
        if (isRadioSingle && useCartStore().cart.items.length > 0) {
          CartService.emptyCart();
        }
        CartService.addToCart(this.cartItem);
        if (isDeactivating) {
          useUpselledStore().downgradeChoice(upsellChoice);
        } else {
          useUpselledStore().upgradeChoice(parentChoice, upsellChoice);
        }
      }
    },
  },
};
</script>
